/**
 * E2E stub for JaakDocumentCapture (plan 08 F1).
 *
 * Activado solo cuando `import.meta.env.VITE_E2E === 'true'` desde el
 * componente real. Renderea botones manuales para que los tests Playwright
 * controlen el flujo (complete / error / cancel) en vez de depender del
 * SDK biométrico real.
 *
 * Diseño:
 *   - Botones con data-testid estables (no traducidos) para selectores
 *     deterministas en Playwright.
 *   - Los callbacks reciben payloads mínimos que satisfacen el contrato
 *     de tipos (JaakCaptureResult) sin imágenes reales — los tests no
 *     deberían inspeccionar el contenido base64, solo validar que el
 *     callback se llamó.
 *
 * NUNCA debe llegar a producción — el branch que lo activa en el componente
 * real es `import.meta.env.VITE_E2E === 'true'`, tree-shakeado por Vite
 * cuando ese env está unset o false (build de prod).
 */
import type { JaakCaptureResult } from '@tankef/core';

import type { JaakDocumentCaptureProps } from '../JaakDocumentCapture';

// Genera un dataURL JPEG sintético via canvas. Necesario para que el
// consumer pueda convertirlo a File con `dataURLtoFile` y subirlo al BE
// (tests E2E mockean la API, pero dev local usando VITE_KYC_LOCAL_STUB
// pega al BE real y necesita un blob válido).
function syntheticDataUrl(label: string, color: string): string {
  if (typeof document === 'undefined') {
    return 'data:image/png;base64,STUB';
  }
  const canvas = document.createElement('canvas');
  canvas.width = 640;
  canvas.height = 400;
  const ctx = canvas.getContext('2d');
  if (!ctx) return 'data:image/png;base64,STUB';
  ctx.fillStyle = color;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = '#ffffff';
  ctx.font = '32px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(label, canvas.width / 2, canvas.height / 2);
  return canvas.toDataURL('image/jpeg', 0.9);
}

function buildStubResult(): JaakCaptureResult {
  const front = syntheticDataUrl('INE FRONT (stub)', '#3498db');
  const back = syntheticDataUrl('INE BACK (stub)', '#1abc9c');
  return {
    front: { fullFrame: front, cropped: front },
    back: { fullFrame: back, cropped: back },
    metadata: { totalImages: 2, processCompleted: true },
    timestamp: new Date().toISOString(),
  };
}

export function JaakDocumentCaptureStub(props: JaakDocumentCaptureProps) {
  return (
    <div
      data-testid="jaak-document-capture-stub"
      style={{
        padding: 20,
        border: '2px dashed #888',
        borderRadius: 8,
        textAlign: 'center',
      }}
    >
      <p style={{ margin: 0, marginBottom: 12 }}>
        [E2E STUB] JaakDocumentCapture — controles manuales para tests
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
        <button
          type="button"
          data-testid="jaak-document-capture-stub-complete"
          onClick={() => props.onCaptureComplete(buildStubResult())}
        >
          Complete capture
        </button>
        <button
          type="button"
          data-testid="jaak-document-capture-stub-error"
          onClick={() => props.onError?.('E2E_STUB_ERROR')}
        >
          Trigger error
        </button>
        <button
          type="button"
          data-testid="jaak-document-capture-stub-cancel"
          onClick={() => props.onCancel?.()}
        >
          Cancel
        </button>
      </div>
    </div>
  );
}
